import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { getMovieById, getRelatedMovies, categories } from '@/mocks/movies';

const categoryNames: Record<string, string> = {};
categories.forEach(c => { categoryNames[c.slug] = c.name; });

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-16 text-center min-h-[60vh] flex flex-col items-center justify-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-200 rounded-full">
            <i className="ri-error-warning-line text-foreground-400 text-2xl"></i>
          </div>
          <p className="text-foreground-500 mb-4">电影不存在或已下架</p>
          <Link to="/" className="whitespace-nowrap px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors">
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const relatedMovies = getRelatedMovies(movie);

  return (
    <>
      <Navbar />
      <main className="pt-20 pb-16">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryNames[movie.category] || movie.category, href: `/category/${movie.category}` },
              { label: movie.title },
            ]}
          />

          <article className="mb-12">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0 w-full sm:w-[300px] mx-auto lg:mx-0">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    className="w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-6">{movie.titleEn}</p>

                <div className="flex flex-wrap items-center gap-4 mb-6">
                  <div className="flex items-center gap-1.5 bg-accent-100 text-accent-800 px-3 py-1.5 rounded-full">
                    <i className="ri-star-fill text-sm"></i>
                    <span className="text-sm font-bold">{movie.rating}</span>
                    <span className="text-xs">/ 10</span>
                  </div>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-eye-line mr-1"></i>{movie.views.toLocaleString()} 次播放
                  </span>
                  {movie.isNew && (
                    <span className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">
                      NEW
                    </span>
                  )}
                  {movie.isHot && (
                    <span className="bg-accent-100 text-accent-700 px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">
                      <i className="ri-fire-fill"></i> 热播
                    </span>
                  )}
                </div>

                <div className="space-y-3 mb-8">
                  <div className="flex items-start gap-4">
                    <span className="text-sm text-foreground-500 flex-shrink-0 w-14">导演</span>
                    <span className="text-sm text-foreground-900">{movie.director}</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="text-sm text-foreground-500 flex-shrink-0 w-14">主演</span>
                    <span className="text-sm text-foreground-900">{movie.actors.join(' / ')}</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="text-sm text-foreground-500 flex-shrink-0 w-14">类型</span>
                    <span className="text-sm text-foreground-900">{categoryNames[movie.category]}</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="text-sm text-foreground-500 flex-shrink-0 w-14">地区</span>
                    <span className="text-sm text-foreground-900">{movie.region}</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="text-sm text-foreground-500 flex-shrink-0 w-14">片长</span>
                    <span className="text-sm text-foreground-900">{movie.duration}</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="text-sm text-foreground-500 flex-shrink-0 w-14">上映</span>
                    <span className="text-sm text-foreground-900">{movie.releaseDate}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 mb-8">
                  {movie.tags.map((tag) => (
                    <span key={tag} className="bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full text-xs whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="whitespace-nowrap px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors inline-flex items-center justify-center gap-2">
                    <i className="ri-play-circle-fill text-lg"></i>
                    在线观看
                  </button>
                  <button className="whitespace-nowrap px-8 py-3 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors inline-flex items-center justify-center gap-2">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">剧情简介</h2>
              <p className="text-sm text-foreground-700 leading-relaxed max-w-3xl">
                {movie.description}
              </p>
            </div>
          </article>

          {relatedMovies.length > 0 && (
            <section>
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Movie",
            "name": movie.title,
            "description": movie.description,
            "image": movie.poster,
            "director": { "@type": "Person", "name": movie.director },
            "actor": movie.actors.map((a) => ({ "@type": "Person", "name": a })),
            "dateCreated": movie.releaseDate,
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": movie.rating,
              "bestRating": "10",
            },
            "url": `https://6080dy.com/article/${movie.id}`,
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://6080dy.com" },
                { "@type": "ListItem", "position": 2, "name": categoryNames[movie.category] || movie.category, "item": `https://6080dy.com/category/${movie.category}` },
                { "@type": "ListItem", "position": 3, "name": movie.title },
              ],
            },
          }),
        }}
      />
      <Footer />
    </>
  );
}